<script>
import modalEditGroup from "../modals/modal-editgroup.vue";
import { useApproverGroups } from "../../stores/modules/approverGroups";

export default {
  name: "groupList",
  components: {
    modalEditGroup,
  },
  data() {
    return {
      groupStore: null,
      groupIdToDelete: 0,
      fields: [
        { key: "name", label: "Name", sortable: true },
        { key: "members", label: "Contacts" },
        {
          key: "edit",
          label: "",
          tdClass: "action-cell",
          thClass: "action-cell",
        },
        {
          key: "menu",
          label: "",
          tdClass: "action-cell action-cell-menu",
          thClass: "action-cell action-cell-menu",
        },
      ],
    };
  },
  async mounted() {
    this.groupStore = useApproverGroups();
    await this.groupStore.loadGroups();

    const self = this;
    this.$root.$on("delete::group::confirmed", async () => {
      if (!self.groupIdToDelete) return;
      await self.$A.ApproverGroupService.Delete(self.groupIdToDelete);
      self.groupIdToDelete = 0;
      await self.groupStore.loadGroups();
    });
  },
  computed: {
    groups() {
      return this.groupStore ? this.groupStore.groups : [];
    },
  },
  methods: {
    createGroup() {
      this.$refs.editGroupModal.open(null);
    },
    editGroup(group) {
      this.$refs.editGroupModal.open(group);
    },
    queueGroupDelete(groupId) {
      this.groupIdToDelete = groupId;
    },
    async handleGroupSaved() {
      await this.groupStore.loadGroups();
    },
  },
};
</script>

<template>
  <div class="group-list-wrapper">
    <b-card class="group-list-card">
      <b-card-body>
        <!-- Toolbar -->
        <div class="card-toolbar">
          <div class="toolbar-left">
            <div class="toolbar-count">
              {{ groups.length }}
              {{ groups.length === 1 ? "group" : "groups" }}
            </div>
          </div>
          <div class="toolbar-right">
            <a
              id="group_button"
              class="button button-light button-green groups-btn mb-0"
              @click="createGroup"
            >
              Create Group
            </a>
          </div>
        </div>

        <!-- Table -->
        <b-table
          v-if="groups.length"
          id="group_table"
          class="group-table mb-0"
          :items="groups"
          :fields="fields"
          responsive
          hover
          tbody-tr-class="group-row"
          @row-clicked="editGroup"
        >
          <template #head()="data">
            <span class="column-header-text">{{ data.label }}</span>
          </template>
          <template #cell(members)="data">
            {{ (data.item.approverIds || []).length }}
            {{ (data.item.approverIds || []).length === 1 ? "contact" : "contacts" }}
          </template>
          <template #cell(edit)="data">
            <button class="row-edit-btn" @click.stop="editGroup(data.item)">
              Edit
            </button>
          </template>
          <template #cell(menu)="data">
            <span @click.stop>
              <b-dropdown
                variant="link"
                toggle-class="row-menu-toggle"
                menu-class="row-menu"
                no-caret
                right
                no-flip
                boundary="viewport"
                offset="4"
              >
                <template #button-content>
                  <b-icon-three-dots-vertical />
                  <span class="sr-only">More actions</span>
                </template>
                <b-dropdown-item
                  :data-cy="`delete-group-${data.item.name}`"
                  v-b-modal.deleteGroupModal
                  @click="queueGroupDelete(data.item.id)"
                >
                  Delete
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </template>
        </b-table>

        <!-- Empty state -->
        <div class="null-item" v-else>
          <h4>You Do Not Currently Have Any Approval Groups</h4>
          <p>
            Groups let you add a set of contacts to a workflow all at once.
            Create one with the "Create Group" button above.
          </p>
        </div>

        <!-- Modals -->
        <modal
          name="deleteGroupModal"
          eventToEmitOnConfirmation="delete::group::confirmed"
          question="Delete This Group?"
          body="Are you sure you want to delete this group? Contacts in the group are not deleted. You cannot undo this action."
          confirm-button-text="Yes, Delete"
        />
        <modalEditGroup ref="editGroupModal" @groupSaved="handleGroupSaved" />
      </b-card-body>
    </b-card>
  </div>
</template>

<style scoped>
.group-list-wrapper {
  background-color: #eef2f5;
  padding: 24px 24px 48px;
}

.group-list-card {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0;
}

::v-deep .group-list-card .card-body {
  padding: 0px;
}

.card-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  border-bottom: 1px solid #e5e7eb;
  gap: 16px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1 1 auto;
  min-width: 0;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toolbar-count {
  font-family: "Open Sans";
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}

::v-deep .group-table table {
  margin-bottom: 0;
}

::v-deep .group-table table th {
  background: #f9fafb;
  border-top: 0;
  border-bottom: 1px solid #e5e7eb;
  padding: 10px 24px !important;
  vertical-align: middle !important;
}

::v-deep .group-table table td {
  padding: 14px 24px !important;
  vertical-align: middle !important;
  border-top: 1px solid #f1f2f4;
  border-bottom: 0;
}

::v-deep .group-table tbody tr:first-child td {
  border-top: 0;
}

::v-deep .group-table table td.action-cell,
::v-deep .group-table table th.action-cell {
  width: 1%;
  white-space: nowrap;
  text-align: right;
  padding-left: 4px !important;
  padding-right: 4px !important;
}

::v-deep .group-table table td.action-cell-menu,
::v-deep .group-table table th.action-cell-menu {
  padding-left: 0 !important;
  padding-right: 16px !important;
}

::v-deep .group-row {
  cursor: pointer;
}

::v-deep .group-table.table-hover tbody tr:hover,
::v-deep .group-table tbody tr:hover {
  background-color: #f9fafb;
  color: inherit;
}

.column-header-text {
  font-family: "Open Sans";
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.row-edit-btn {
  background: transparent;
  border: 1px solid #d1d5db;
  color: #1b1b1c;
  font-size: 14px;
  font-weight: 500;
  padding: 6px 16px;
  border-radius: 6px;
  line-height: 20px;
}

.row-edit-btn:hover {
  background: #ffffff;
  border-color: #b4b9c0;
}

::v-deep .row-menu-toggle {
  color: #6b7280;
  padding: 4px 6px;
  border-radius: 4px;
}

::v-deep .row-menu-toggle:hover,
::v-deep .row-menu-toggle:focus {
  color: #1b1b1c;
  background-color: #f1f2f4;
  text-decoration: none;
  box-shadow: none;
}

::v-deep .row-menu {
  min-width: 8rem;
  font-size: 14px;
}

.groups-btn {
  padding: 9px 17px !important;
  font-weight: normal;
  height: 38px;
  background: #019256;
  box-shadow: 0px 1px 2px rgba(8, 11, 13, 0.05);
  border-radius: 6px;
}

.null-item {
  list-style-type: none;
  text-align: center;
  padding: 48px 16px;
}

.null-item h4 {
  margin-bottom: 15px;
}

@media (max-width: 576px) {
  .group-list-wrapper {
    padding: 12px 12px 32px;
  }

  .card-toolbar {
    padding: 12px 16px;
    flex-direction: column;
    align-items: stretch;
  }

  .toolbar-left,
  .toolbar-right {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
